import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubSidebarWrapper } from "../../../Components/Sidebar/Styles";
import {
  PanelHeader,
  PanelTitle,
  ProgressLabel,
  StepsBar,
  StepsBarTrack,
  StepsBarFill,
  StepBullet,
  StepBulletWrapper,
  StepTooltip,
  PanelBody,
  FieldWrapper,
  FieldLabel,
  FieldInput,
  FieldTextarea,
  FieldHelpText,
  BooleanToggleRow,
  PanelFooter,
  SecondaryButton,
  SavedIndicator,
} from "./Styles";
import { FiCheck } from "react-icons/fi";
import { Button } from "../../../Components/Button";
import { Contract, ContractVariableDefinition, ContractVariableInputType } from "../../../Common/Types-DTOs/Contracts";
import { useContracts } from "../../../Common/Hooks/useContracts";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";
import { CANONICAL_CCAA_NAMES } from "@dd-report-components";
import { AddressInput } from "./AddressInput";
import { applyAddressAutofill } from "./addressAutofill";
import { AddressSuggestion } from "../../../Common/Types-DTOs/Address";

const sortedCcaaOptions = (ccaaValues: ContractVariableDefinition["ccaaValues"]): string[] => {
  const known = new Set((ccaaValues ?? []).map((cv) => cv.ccaa));
  return CANONICAL_CCAA_NAMES.filter((ccaa) => known.has(ccaa));
};

interface ContractVariablesPanelProps {
  contract: Contract;
  onSaved: (updated: Contract) => void;
}

const htmlInputType = (inputType: ContractVariableInputType): string => {
  if (inputType === "date") return "date";
  if (inputType === "number" || inputType === "currency") return "number";
  return "text";
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PLAIN_NUMBER_RE = /^-?\d+(\.\d+)?$/;

// Chat can save a date/amount as free text ("1 de agosto del 26", "1200€") —
// perfectly fine for the rendered clause, but a native <input type="date">
// or <input type="number"> silently shows BLANK for any value that doesn't
// match its strict format, making already-saved data look missing. Falling
// back to a plain text input for those values shows what's actually there;
// it stays native (with the date picker / spinner) for anything typed
// directly into this form, since a fresh empty or well-formed value passes.
const rendersNatively = (inputType: ContractVariableInputType, value: string): boolean => {
  if (!value) return true;
  if (inputType === "date") return ISO_DATE_RE.test(value);
  if (inputType === "number" || inputType === "currency") return PLAIN_NUMBER_RE.test(value);
  return true;
};

export const ContractVariablesPanel = ({ contract, onSaved }: ContractVariablesPanelProps) => {
  const { t } = useTranslation();
  const { updateVariables } = useContracts();

  const primaryCcaaKey = useMemo(() => {
    const ccaaVariables = contract.variableSchema
      .filter((v) => v.inputType === "ccaa")
      .sort((a, b) => a.order - b.order);
    return ccaaVariables[0]?.key;
  }, [contract.variableSchema]);

  const nonPrimaryCcaaKeys = useMemo(
    () =>
      contract.variableSchema
        .filter((v) => v.inputType === "ccaa" && v.key !== primaryCcaaKey)
        .map((v) => v.key),
    [contract.variableSchema, primaryCcaaKey]
  );

  const groups = useMemo(() => {
    const visibleVariables = contract.variableSchema.filter(
      (v) => !nonPrimaryCcaaKeys.includes(v.key)
    );
    const byGroup = new Map<string, ContractVariableDefinition[]>();
    for (const variable of visibleVariables) {
      const list = byGroup.get(variable.group) ?? [];
      list.push(variable);
      byGroup.set(variable.group, list);
    }
    return Array.from(byGroup.entries())
      .map(([group, variables]) => ({
        group,
        variables: [...variables].sort((a, b) => a.order - b.order),
      }))
      .sort((a, b) => a.variables[0].order - b.variables[0].order);
  }, [contract.variableSchema, nonPrimaryCcaaKeys]);

  const firstIncompleteStepIndex = (variables: Record<string, string>): number => {
    const index = groups.findIndex((g) =>
      g.variables.some((v) => v.required && !variables[v.key]?.trim())
    );
    return index === -1 ? groups.length - 1 : index;
  };

  const [stepIndex, setStepIndex] = useState(() => firstIncompleteStepIndex(contract.variables));
  const [formValues, setFormValues] = useState<Record<string, string>>(contract.variables);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // The chat can fill in variables on this same contract in the background
  // (fill_contract_variables) — when that happens, `contract` arrives here as
  // a new prop (via the CONTRACT_VARIABLES_UPDATED refetch in ContractDetails)
  // and the form needs to actually show those values and jump to whatever
  // step still needs data, instead of sitting frozen on stale local state.
  // Never jumps backward, so it never fights manual navigation via the step
  // bullets.
  useEffect(() => {
    setFormValues(contract.variables);
    setStepIndex((prev) => Math.max(prev, firstIncompleteStepIndex(contract.variables)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  if (groups.length === 0) return null;

  const currentGroup = groups[stepIndex];
  const isLastStep = stepIndex === groups.length - 1;
  const percent = groups.length > 1 ? (stepIndex / (groups.length - 1)) * 100 : 100;

  const handleFieldChange = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddressSelect = (variable: ContractVariableDefinition, suggestion: AddressSuggestion) => {
    setFormValues((prev) =>
      applyAddressAutofill(variable, suggestion, contract.variableSchema, primaryCcaaKey, prev)
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Never resend the hidden, non-primary "ccaa" keys' last-known values —
    // the backend always recomputes them by fan-out from the primary key, so
    // resubmitting a stale copy here could otherwise race with (and revert)
    // a just-changed primary answer.
    const submittedValues = Object.fromEntries(
      Object.entries(formValues).filter(([key]) => !nonPrimaryCcaaKeys.includes(key))
    );
    const result = await updateVariables(contract.id, submittedValues);
    setIsSaving(false);

    if (isErr(result)) {
      eventBus.emit("NOTIFICATION", Notification.buildNotification("error", t("contracts.variablesPanel.notifications.saveFailed")));
      return false;
    }

    onSaved(result.value.contract);

    if (result.value.rejected.length > 0) {
      const fieldNames = result.value.rejected.map((r) => r.label).join(", ");
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("contracts.variablesPanel.notifications.invalidFields", { fields: fieldNames }))
      );
      return false;
    }

    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2000);
    return true;
  };

  const handleNext = async () => {
    const saved = await handleSave();
    if (saved && !isLastStep) setStepIndex((i) => i + 1);
  };

  const handlePrevious = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleStepClick = (index: number) => {
    setStepIndex(index);
  };

  return (
    <SubSidebarWrapper>
      <PanelHeader>
        <PanelTitle>{t("contracts.variablesPanel.title")}</PanelTitle>
        <ProgressLabel>
          {t("contracts.variablesPanel.progressLabel", {
            current: stepIndex + 1,
            total: groups.length,
            groupName: currentGroup.group,
          })}
        </ProgressLabel>
        <StepsBar>
          <StepsBarTrack />
          <StepsBarFill $percent={percent} />
          {groups.map((group, index) => (
            <StepBulletWrapper key={group.group}>
              <StepBullet
                type="button"
                $state={index < stepIndex ? "done" : index === stepIndex ? "current" : "upcoming"}
                onClick={() => handleStepClick(index)}
              />
              <StepTooltip>{group.group}</StepTooltip>
            </StepBulletWrapper>
          ))}
        </StepsBar>
      </PanelHeader>

      <PanelBody>
        {currentGroup.variables.map((variable) => (
          <FieldWrapper key={variable.key}>
            <FieldLabel>
              {variable.label}
              {variable.required ? t("contracts.variablesPanel.requiredMarker") : ""}
            </FieldLabel>
            {variable.inputType === "boolean" ? (
              <BooleanToggleRow>
                <input
                  type="checkbox"
                  checked={formValues[variable.key] === "true"}
                  onChange={(e) => handleFieldChange(variable.key, e.target.checked ? "true" : "false")}
                />
                {t("contracts.variablesPanel.booleanYesLabel")}
              </BooleanToggleRow>
            ) : variable.inputType === "ccaa" ? (
              <FieldInput
                as="select"
                value={formValues[variable.key] ?? ""}
                onChange={(e) => handleFieldChange(variable.key, e.target.value)}
              >
                <option value="" disabled>
                  {t("contracts.variablesPanel.ccaaPlaceholder")}
                </option>
                {sortedCcaaOptions(variable.ccaaValues).map((ccaa) => (
                  <option key={ccaa} value={ccaa}>
                    {ccaa}
                  </option>
                ))}
              </FieldInput>
            ) : variable.inputType === "address" ? (
              <AddressInput
                value={formValues[variable.key] ?? ""}
                onChange={(value) => handleFieldChange(variable.key, value)}
                onSelect={(suggestion) => handleAddressSelect(variable, suggestion)}
              />
            ) : variable.inputType === "textarea" ? (
              <FieldTextarea
                value={formValues[variable.key] ?? ""}
                onChange={(e) => handleFieldChange(variable.key, e.target.value)}
              />
            ) : (
              <FieldInput
                type={
                  rendersNatively(variable.inputType, formValues[variable.key] ?? "")
                    ? htmlInputType(variable.inputType)
                    : "text"
                }
                min={variable.inputType === "number" || variable.inputType === "currency" ? 0 : undefined}
                value={formValues[variable.key] ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Días de pago, importes, etc. no tienen sentido en negativo en
                  // ningún contrato de este catálogo — se bloquea el signo en origen
                  // en vez de solo advertir, para que nunca llegue a la cláusula renderizada.
                  const isNegativeNumeric =
                    (variable.inputType === "number" || variable.inputType === "currency") && value.includes("-");
                  const containsEuroSymbol = variable.inputType === "currency" && value.includes("€");
                  if (isNegativeNumeric || containsEuroSymbol) return;
                  handleFieldChange(variable.key, value);
                }}
              />
            )}
            {variable.helpText && <FieldHelpText>{variable.helpText}</FieldHelpText>}
          </FieldWrapper>
        ))}
      </PanelBody>

      <PanelFooter>
        <SecondaryButton onClick={handlePrevious} disabled={stepIndex === 0}>
          {t("contracts.variablesPanel.previousButton")}
        </SecondaryButton>
        <SavedIndicator $visible={justSaved}>
          <FiCheck size={16} />
        </SavedIndicator>
        <Button onClick={handleNext} isLoading={isSaving}>
          {isLastStep ? t("contracts.variablesPanel.saveButton") : t("contracts.variablesPanel.nextButton")}
        </Button>
      </PanelFooter>
    </SubSidebarWrapper>
  );
};
